import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarCheck, Loader2, XCircle, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";

const tone = { New: "bg-brand-accent/15 text-brand-accent-dark", Confirmed: "bg-emerald-100 text-emerald-800", Completed: "bg-brand-sand text-brand-muted", Cancelled: "bg-red-50 text-red-700" };
const fmt = (d) => new Date(d + "T00:00:00").toLocaleDateString("en-IN", { dateStyle: "medium" });

function Reschedule({ b, opts, onDone, onCancel }) {
  const [f, setF] = useState({ date: opts.min_date, time_window: b.time_window });
  const [busy, setBusy] = useState(false);
  const full = opts.full_slots?.[f.date] || [];
  const blocked = opts.blocked_dates?.includes(f.date);
  const submit = async () => {
    setBusy(true);
    try { onDone((await api.post(`/bookings/${b.id}/reschedule`, f)).data); toast.success("New slot requested — homiin will confirm shortly"); }
    catch (e) { toast.error(formatErr(e)); }
    setBusy(false);
  };
  return (
    <div className="mt-4 rounded-xl bg-brand-sand p-4 space-y-3" data-testid={`reschedule-form-${b.id}`}>
      <div className="flex flex-wrap gap-3 items-center">
        <input type="date" min={opts.min_date} max={opts.max_date} value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} className="rounded-md border border-brand-line bg-white px-2.5 py-1.5 text-sm" data-testid={`reschedule-date-${b.id}`} />
        {opts.time_windows.map((w) => (
          <button key={w} type="button" disabled={full.includes(w)} onClick={() => setF({ ...f, time_window: w })} data-testid={`reschedule-window-${b.id}-${w.split(" ")[0].toLowerCase()}`}
            className={`rounded-full border px-3 py-1.5 text-xs transition-colors ${full.includes(w) ? "line-through opacity-50 cursor-not-allowed border-brand-line" : f.time_window === w ? "bg-brand-ink text-brand-cream border-brand-ink" : "border-brand-line hover:border-brand-accent"}`}>{w}{full.includes(w) ? " · Full" : ""}</button>
        ))}
      </div>
      {blocked && <p className="text-xs text-brand-accent" data-testid={`reschedule-blocked-${b.id}`}>homiin isn't visiting on this date.</p>}
      <div className="flex gap-3">
        <button onClick={submit} disabled={busy || blocked || full.includes(f.time_window)} className="rounded-full bg-brand-accent hover:bg-brand-accent-dark disabled:opacity-50 text-white px-4 py-2 text-xs inline-flex items-center gap-1" data-testid={`reschedule-submit-${b.id}`}>{busy && <Loader2 className="w-3 h-3 animate-spin" />} Request this slot</button>
        <button onClick={onCancel} className="text-xs text-brand-muted hover:text-brand-ink" data-testid={`reschedule-cancel-${b.id}`}>Never mind</button>
      </div>
    </div>
  );
}

export default function MyBookings() {
  const [rows, setRows] = useState(null);
  const [opts, setOpts] = useState(null);
  const [editing, setEditing] = useState(null);
  useEffect(() => {
    api.get("/bookings/mine").then((r) => setRows(r.data)).catch(() => setRows([]));
    api.get("/bookings/options").then((r) => setOpts(r.data)).catch(() => {});
  }, []);
  if (!rows || rows.length === 0) return null;

  const replace = (nb) => { setRows(rows.map((r) => (r.id === nb.id ? nb : r))); setEditing(null); api.get("/bookings/options").then((r) => setOpts(r.data)).catch(() => {}); };
  const cancel = async (b) => {
    if (!window.confirm("Cancel this site visit?")) return;
    try { replace((await api.post(`/bookings/${b.id}/cancel`)).data); toast.success("Visit cancelled"); } catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <section className="mt-14" data-testid="my-bookings">
      <p className="text-xs uppercase tracking-[0.25em] text-brand-accent inline-flex items-center gap-2"><CalendarCheck className="w-3.5 h-3.5" /> Site visits</p>
      <div className="grid md:grid-cols-2 gap-4 mt-4">
        {rows.map((b) => {
          const open = ["New", "Confirmed"].includes(b.status);
          return (
            <div key={b.id} className="rounded-2xl border border-brand-line bg-brand-cream p-6" data-testid={`my-booking-${b.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-serif-display text-2xl text-brand-ink">{b.visit_type}</p>
                  <p className="text-sm text-brand-muted mt-1">{fmt(b.date)} · {b.time_window}</p>
                  {b.rescheduled_from && <p className="text-[11px] text-brand-muted-light" data-testid={`my-booking-rescheduled-${b.id}`}>Moved from {fmt(b.rescheduled_from.date)} · {b.rescheduled_from.time_window}</p>}
                  <Link to={`/quote/${b.quote_id}`} className="text-xs font-mono-num text-brand-accent hover:text-brand-accent-dark" data-testid={`my-booking-quote-${b.id}`}>{b.quote_number}</Link>
                </div>
                <span className={`text-[10px] uppercase tracking-[0.15em] px-3 py-1 rounded-full shrink-0 ${tone[b.status]}`} data-testid={`my-booking-status-${b.id}`}>{b.status}</span>
              </div>
              <p className="text-xs text-brand-muted-light mt-3">{b.status === "New" ? "Awaiting confirmation from homiin." : b.status === "Confirmed" ? "Confirmed — our designer will see you then." : b.status === "Completed" ? "Visit completed. Thank you!" : "This visit was cancelled."}</p>
              {open && editing !== b.id && (
                <div className="flex gap-2 mt-4">
                  <button onClick={() => setEditing(b.id)} disabled={!opts} className="inline-flex items-center gap-1 rounded-full border border-brand-line hover:border-brand-accent px-4 py-2 text-xs" data-testid={`my-booking-reschedule-${b.id}`}><RefreshCw className="w-3 h-3" /> Request new slot</button>
                  <button onClick={() => cancel(b)} className="inline-flex items-center gap-1 rounded-full border border-brand-line hover:border-red-300 hover:text-red-700 px-4 py-2 text-xs text-brand-muted" data-testid={`my-booking-cancel-${b.id}`}><XCircle className="w-3 h-3" /> Cancel</button>
                </div>
              )}
              {editing === b.id && opts && <Reschedule b={b} opts={opts} onDone={replace} onCancel={() => setEditing(null)} />}
            </div>
          );
        })}
      </div>
    </section>
  );
}
